'use client';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useI18n } from '@fshr/context/i18n-context';
import Button from '../button';
import Icon, { IconAlert } from '../icon';
import Input from '../input';
import Link from '../link';
import LoadingIndicator from '../loading-indicator';
import { useForm } from '@fshr/hooks';
import { useTealium } from '@fshr/context/tealium-context';

interface InputProps {
	id: number;
	initialValue: string;
	display: boolean;
}

type CancelReservationFormProps = {
	defaultEmail?: string;
	cancelFormCtaLink?: string;
	includeConfirmationInForm?: boolean;
	confirmationCTALink?: string;
	confirmationCTAText?: string;
	onSubmit: (formValues: { [key: string]: string }) => Promise<Response>;
};

export default function CancelReservationForm({
	defaultEmail = '',
	cancelFormCtaLink,
	includeConfirmationInForm = false,
	confirmationCTALink,
	confirmationCTAText,
	onSubmit,
}: CancelReservationFormProps): ReactElement {
	const { t } = useI18n();
	const [inputs, setInputs] = useState<InputProps[]>(
		[1, 2, 3, 4, 5].map<InputProps>((v, index) => ({ id: index, display: false, initialValue: '' }))
	);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const { trackPage } = useTealium();
	const errorRef = useRef<HTMLDivElement | null>(null);

	const onSubmitForm = async (formValues: { [key: string]: string }): Promise<void> => {
		try {
			setIsLoading(true);
			setIsSubmitted(true);
			const response = await onSubmit(formValues);
			setIsSuccess(response?.ok);
		} catch (error) {
			setIsSuccess(false);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (!isSuccess && isSubmitted) {
			errorRef?.current?.focus();
		}
	}, [isSuccess, isSubmitted, isLoading]);

	useEffect(() => {
		if (includeConfirmationInForm && isSuccess) {
			trackPage({
				page_detail: 'cancel_reservation:confirmation',
				form_name: 'cancel reservation',
				event_name: 'form_submit',
			});
		}
	}, [includeConfirmationInForm, isSuccess]);

	const { handleSubmit, registerField, unregisterField } = useForm({
		onSubmit: onSubmitForm,
	});

	const addInput = () => {
		const hiddenInputIndex = inputs.findIndex(v => !v.display);
		if (hiddenInputIndex !== -1) {
			const updatedInputs = inputs.map((v, i) => {
				if (i === hiddenInputIndex) return { ...v, display: true };
				return v;
			});
			setInputs(updatedInputs);
		}
	};

	const removeInput = (id: number) => {
		const updatedInputs = inputs.filter(value => value.id !== id);
		updatedInputs.push({ id, display: false, initialValue: '' });
		setInputs(updatedInputs);
		unregisterField({ name: `email${id}` });
	};

	return isLoading ? (
		<LoadingIndicator className="relative flex-1" />
	) : includeConfirmationInForm && isSuccess ? (
		<div className="flex justify-center items-center lg:mt-[100px]">
			<div className="lg:w-[434px] flex flex-col spacing-stack-l text-center">
				<div>
					<p className="text-h4">{t('Cancellation confirmation')}</p>
				</div>
				<div>
					<p className="text-body3">
						{t(
							"Your reservation has been cancelled. Please don't hesitate to contact us with any questions or further details."
						)}
					</p>
				</div>
				{confirmationCTALink && (
					<div>
						<Link
							href={confirmationCTALink}
							type="submit"
							intent="filled"
							className="w-auto text-button2"
						>
							{confirmationCTAText}
						</Link>
					</div>
				)}
			</div>
		</div>
	) : (
		<div className="container mx-auto lg:max-w-[930px] xl:max-w-[1530px] overflow-x-hidden">
			<h3 className="text-h3 mb-300 lg:mb-200">{t('Cancel Reservation')}</h3>
			<div className="mx-auto lg:max-w-[930px] xl:max-w-[1530px] mb-[70px] overflow-x-hidden">
				<div className="spacing-heading-m">
					<p className="text-body3">
						{t(
							'Cancellation confirmation email will be sent to your primary email address. You can send cancellation confirmation emails to up to five email addresses by clicking on the link below.'
						)}
					</p>
				</div>
				<form noValidate={true} onSubmit={handleSubmit}>
					<div className="flex flex-col spacing-stack-l">
						<div className="flex flex-col gap-[2px]">
							<p className="text-label text-text-secondary">{t('Primary Email')}</p>
							<p className="text-body2">{defaultEmail}</p>
						</div>
						{inputs.map((input, index) =>
							input.display ? (
								<React.Fragment key={index}>
									<div className="flex-col spacing-heading-xs">
										<div className="flex justify-between">
											<p className="text-subtitle2">{t('Additional Email {0}', [index + 1])}</p>
											<p
												aria-disabled={inputs.length >= 5}
												className="text-button2 cursor-pointer underline underline-offset-[3px]"
												onClick={() => {
													removeInput(input.id);
												}}
											>
												{t('Remove')}
											</p>
										</div>
										<div className="flex">
											<Input
												key={input.id}
												id={`email${input.id}`}
												placeholder={t('Email')}
												label={t('Email')}
												{...registerField({
													name: `email${input.id}`,
													initialValue: input.initialValue || '',
													type: 'email',
													options: {
														required: false,
														errorMessages: {
															empty: t('Email is required.'),
															partial: t('Please enter a valid email address.'),
														},
														analyticsErrorMessages: {
															empty: 'Email is required.',
															partial: 'Please enter a valid email address.',
														},
													},
												})}
											/>
										</div>
									</div>
								</React.Fragment>
							) : null
						)}
						{inputs.some(input => !input.display) ? (
							<div className="flex gap-100 items-center">
								<Icon className="!no-underline" icon="plus_sign" />
								<Button
									onClick={addInput}
									intent="unstyled"
									className="flex text-button1 underline underline-offset-[3px] cursor-pointer"
									aria-label={t('Add Email Recipient')}
								>
									{t('Add Email Recipient')}
								</Button>
							</div>
						) : null}
						<div tabIndex={-1} ref={errorRef} className="border-b border-b-border-subtle"></div>
						{!isSuccess && isSubmitted ? (
							<div className="flex bg-error-container-subtle rounded-[5px] p-100 gap-100">
								<div>
									<IconAlert />
								</div>
								<div>
									<p className="text-default">
										{t(
											'We are unable to cancel your reservation online, please contact the Hotel or Worldwide Reservations for assistance.'
										)}
									</p>
								</div>
							</div>
						) : null}
						<div className="flex flex-col-reverse md:flex-row justify-end gap-150 md:gap-100">
							{cancelFormCtaLink && (
								<Link
									href={cancelFormCtaLink}
									intent="outline"
									className="w-full md:w-auto md:min-w-[130px]"
								>
									{t('cancel')}
								</Link>
							)}
							<Button type="submit" intent="filled" className="w-full md:w-auto md:min-w-[130px]">
								{t('submit')}
							</Button>
						</div>
					</div>
				</form>
			</div>
		</div>
	);
}
